import { useState, type FC } from 'react';
import { Modal, Form, Input, Button, Typography, message } from 'antd';
import { MailOutlined, SendOutlined, CommentOutlined } from '@ant-design/icons';
import { useSiteContent } from '@/context/ContentProvider';

const { Text, Paragraph } = Typography;

interface ResumeModalProps {
  open: boolean;
  onClose: () => void;
}

export const ResumeModal: FC<ResumeModalProps> = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const { resumeModal, validation } = useSiteContent();

  const handleSend = async (values: { email: string; website?: string }) => {
    setSending(true);
    try {
      const res = await fetch('/api/resume?action=requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: values.email, website: values.website }),
      });

      if (!res.ok) throw new Error('Failed to save');

      setSentEmail(values.email);
      setSent(true);
    } catch {
      message.error(resumeModal.errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSent(false);
    setSending(false);
    setSentEmail('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={420}
      transitionName="ant-fade"
      maskTransitionName="ant-fade"
      styles={{ body: { padding: '28px 24px' } }}
    >
      {!sent ? (
        <>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'rgba(176, 150, 125, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <CommentOutlined style={{ fontSize: 24, color: 'var(--color-accent)' }} />
            </div>
            <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 4 }}>
              {resumeModal.title}
            </Text>
            <Paragraph type="secondary" style={{ margin: 0, fontSize: 14 }}>
              {resumeModal.subtitle}
            </Paragraph>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSend}
            requiredMark={false}
            validateTrigger="onBlur"
          >
            {/* Honeypot — hidden from humans, bots auto-fill it */}
            <Form.Item name="website" style={{ position: 'absolute', left: -9999, opacity: 0, height: 0 }} aria-hidden="true">
              <Input tabIndex={-1} autoComplete="off" />
            </Form.Item>

            <Form.Item
              name="email"
              style={{ marginBottom: 24 }}
              rules={[
                { required: true, message: validation.emailRequired },
                { type: 'email', message: validation.emailInvalid },
              ]}
            >
              <Input
                size="large"
                placeholder={resumeModal.placeholder}
                prefix={<MailOutlined style={{ color: 'var(--color-text-muted)' }} />}
                autoFocus
              />
            </Form.Item>
            <br />

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                icon={<SendOutlined />}
                loading={sending}
                style={{ fontWeight: 600, height: 44 }}
              >
                {resumeModal.submitText}
              </Button>
            </Form.Item>
          </Form>
        </>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'rgba(82, 196, 26, 0.1)',
              color: '#52c41a',
              fontSize: 28,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            ✓
          </div>
          <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 4 }}>
            {resumeModal.successTitle}
          </Text>
          <Paragraph type="secondary" style={{ margin: '0 0 20px', fontSize: 14 }}>
            {resumeModal.successSubtitle.replace('{email}', sentEmail)}
          </Paragraph>
          <Button type="primary" onClick={handleClose}>
            {resumeModal.doneText}
          </Button>
        </div>
      )}
    </Modal>
  );
};

/**
 * Triggers a resume PDF download and returns a promise that resolves when the download starts.
 */
export const downloadResume = (): void => {
  const link = document.createElement('a');
  link.href = '/api/resume?action=download';
  link.download = 'Resume.pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
